import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { closeCreateInvitationModal, createGroupSchema, fetchCreateInvitation, selectCreateInvitationModalOpen, selectCurrentRole, selectOnCreate } from "../../model"
import { SubmitHandler, useForm } from "react-hook-form"
import notify from "@/shared/lib/notify"
import { yupResolver } from "@hookform/resolvers/yup"
import { Modal } from "@/shared/ui/modal"
import { FormInput } from "@/shared/ui/form-input"
import { selectEmployeesGroups, selectUsersGroups } from "@/entities/groups"
import { useMemo } from "react"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { LinkType } from "@/shared/api/invitations"

const invitationTypesSelectOptions = [
    {
        value: "qr",
        label: 'QR-Код'
    },
    {
        value: "link",
        label: 'Ссылка'
    }
]

interface CreateInvitationFormValues {
    type: LinkType,
    group_id: string,
    permanent: boolean
}

const initialState: CreateInvitationFormValues = {
    type: "qr",
    group_id: "",
    permanent: false
}

export const CreateInvitationModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectCreateInvitationModalOpen)
    const onCreate = useAppSelector(selectOnCreate)
    const employeesGroups = useAppSelector(selectEmployeesGroups)
    const usersGroups = useAppSelector(selectUsersGroups)
    const role = useAppSelector(selectCurrentRole)

    const groups = useMemo(() => role === 'users' ? usersGroups : employeesGroups, [role, usersGroups, employeesGroups])

    const handleClose = () => {
        dispatch(closeCreateInvitationModal())
    }

    const groupSelectOptions = useMemo(() => groups.map((group) => (
        { value: group.id, label: group.name }
    )), [groups])


    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        await new Promise((res, rej) => {
            dispatch(fetchCreateInvitation({
                type: data.type,
                data: {
                    group_id: data.group_id,
                    permanent: !data.permanent
                }
            }))
                .unwrap()
                .then(() => {
                    notify.success('Приглашение успешно создано')
                    reset()
                    res('')
                    onCreate?.()
                    handleClose()
                })
                .catch((e) => {
                    notify.error(getErrorMessage(e))
                    rej()
                })
        })
    }

    const {
        handleSubmit,
        control,
        register,
        formState: { errors, isSubmitting },
        reset
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(createGroupSchema)
    })

    return (
        <Modal
            isOpen={isOpen}
            title="Создание приглашение"
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <FormInput
                    label={"Тип приглашения"}
                    id={"type"}
                    error={errors['type']}
                    options={invitationTypesSelectOptions}
                    control={control}
                    type="select"
                    required={true}
                    placeholder="Выберите тип приглашения"
                />
                <FormInput
                    label={"Группа"}
                    id={"group_id"}
                    error={errors['group_id']}
                    options={groupSelectOptions}
                    control={control}
                    type="select"
                    required={true}
                    placeholder="Выберите группу"
                />
                <FormInput
                    register={register}
                    label={"Одноразовая"}
                    id={"permanent"}
                    error={errors['permanent']}
                    type="toggle"
                    required={true}
                />
                <div className="w-full flex gap-3">
                    <button onClick={handleClose} className="form_cancel_button">
                        Отменить
                    </button>
                    <button disabled={isSubmitting} className="form_handle_button" type="submit">
                        Добавить
                    </button>
                </div>
            </form>
        </Modal>
    )
}